import { useState } from 'react';
import {
  MessageSquarePlus,
  Settings,
  HelpCircle,
  LogOut,
  Search,
  BookOpen,
  MessageCircleQuestion,
  FileText,
  Bug,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useConversationStore } from '../../stores/conversationStore';
import { useUIStore } from '../../stores/uiStore';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../ui/dropdown-menu';
import { ChatList } from '../chat/ChatList';
import { ReportProblemModal } from '../feedback/ReportProblemModal';
import { ChatHelp } from '../help/ChatHelp';

export function Sidebar() {
  const [reportProblemOpen, setReportProblemOpen] = useState(false);
  const [chatHelpOpen, setChatHelpOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { createConversation, clearActiveConversation } = useConversationStore();
  const { setSidebarOpen, setCurrentView } = useUIStore();

  const handleCreateChat = async () => {
    try {
      await createConversation({
        title: 'Neuer Chat',
        aiProvider: 'n8n',
        modelName: 'n8n-default',
      });
      // Switch to chat view and close sidebar on mobile
      setCurrentView('chat');
      setSidebarOpen(false);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    return user?.email?.substring(0, 2).toUpperCase() || 'U';
  };

  return (
    <div className="flex flex-col h-full bg-card border-r">
      {/* Logo - klickbar für Willkommensseite */}
      <div className="p-4 border-b">
        <button
          onClick={() => {
            clearActiveConversation();
            setCurrentView('chat');
            setSidebarOpen(false);
          }}
          className="cursor-pointer hover:opacity-80 transition-opacity"
        >
          <img src="/logo.png" alt="KI für Pflege" className="h-8 w-auto" />
        </button>
      </div>

      {/* Action Buttons */}
      <div className="p-3 space-y-2 border-b">
        <Button
          variant="default"
          className="w-full justify-start"
          onClick={handleCreateChat}
        >
          <MessageSquarePlus className="mr-2 h-4 w-4" />
          Neuer Chat
        </Button>

        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={() => setCurrentView('search')}
        >
          <Search className="mr-2 h-4 w-4" />
          Suche
        </Button>

        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={() => setChatHelpOpen(true)}
        >
          <MessageCircleQuestion className="mr-2 h-4 w-4" />
          Hilfe
        </Button>
      </div>

      {/* Chat List */}
      <ScrollArea className="flex-1">
        <ChatList />
      </ScrollArea>

      {/* User Menu */}
      <div className="p-3 border-t">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-start p-2 h-auto"
            >
              <Avatar className="h-8 w-8 mr-2">
                <AvatarFallback className="bg-primary text-primary-foreground">{getInitials()}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start flex-1 min-w-0">
                <span className="text-sm font-medium truncate w-full">
                  {user?.displayName || 'Benutzer'}
                </span>
                <span className="text-xs text-muted-foreground truncate w-full">
                  {user?.email}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Mein Konto</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setCurrentView('settings')}>
              <Settings className="mr-2 h-4 w-4" />
              <span>Einstellungen</span>
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <HelpCircle className="mr-2 h-4 w-4" />
                <span>Hilfe</span>
                <ChevronRight className="ml-auto h-4 w-4" />
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setCurrentView('help')}>
                    <BookOpen className="mr-2 h-4 w-4" />
                    <span>Hilfecenter</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setChatHelpOpen(true)}>
                    <MessageCircleQuestion className="mr-2 h-4 w-4" />
                    <span>Chathilfe</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setCurrentView('terms')}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>AGB und Richtlinien</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setReportProblemOpen(true)}>
                    <Bug className="mr-2 h-4 w-4" />
                    <span>Fehler melden</span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Abmelden</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Report Problem Modal */}
      <ReportProblemModal open={reportProblemOpen} onOpenChange={setReportProblemOpen} />

      {/* Chat Help */}
      <ChatHelp open={chatHelpOpen} onOpenChange={setChatHelpOpen} />
    </div>
  );
}
